import { Pipe, PipeTransform, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Pipe({
  name: 'embed',
  standalone: false,
})
export class OEmbedToIframePipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string): SafeHtml {
    if (!value) {
      return value;
    }

    const sanitizedValue =
      this.sanitizer.sanitize(SecurityContext.HTML, value) ?? '';

    const parser = new DOMParser();
    const doc = parser.parseFromString(sanitizedValue, 'text/html');
    const oEmbedElements = doc.querySelectorAll('oembed');

    if (!oEmbedElements.length) {
      return sanitizedValue;
    }

    oEmbedElements.forEach((element) => {
      const url = element.getAttribute('url');
      if (!url) return;

      const embedUrl = this.constructEmbedUrl(url);
      if (!embedUrl) return;
      const iframe = this.generateIframe(embedUrl);
      element.replaceWith(iframe);
    });

    const updatedHtml = doc.body.innerHTML;

    return this.sanitizer.bypassSecurityTrustHtml(updatedHtml);
  }

  private constructEmbedUrl(url: string): string {
    let parsedUrl: URL;
    try {
      parsedUrl = new URL(url);
    } catch {
      return '';
    }
    if (parsedUrl.protocol !== 'https:' && parsedUrl.protocol !== 'http:') {
      return '';
    }
    const hostname = parsedUrl.hostname.toLowerCase();

    if (hostname === 'youtube.com' || hostname === 'www.youtube.com') {
      const videoId = parsedUrl.searchParams.get('v');
      return videoId
        ? `https://www.youtube.com/embed/${encodeURIComponent(videoId)}`
        : '';
    } else if (hostname === 'vimeo.com' || hostname === 'www.vimeo.com') {
      const videoId = this.extractVimeoId(url);
      return videoId
        ? `https://player.vimeo.com/video/${encodeURIComponent(videoId)}`
        : '';
    } else if (hostname === 'facebook.com' || hostname === 'www.facebook.com') {
      return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(
        url
      )}&data_show_text=true`;
    } else if (
      hostname === 'instagram.com' ||
      hostname === 'www.instagram.com'
    ) {
      const postId = this.extractInstagramId(url);
      return postId
        ? `https://www.instagram.com/p/${encodeURIComponent(postId)}/embed/`
        : '';
    }

    // Add more providers if needed
    return '';
  }

  extractVimeoId(url: string): string {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? match[1] : '';
  }

  extractInstagramId(url: string): string {
    const match = url.match(/instagram\.com\/p\/([^/]+)/);
    return match ? match[1] : '';
  }

  private generateIframe(embedUrl: string): HTMLElement {
    const iframe = document.createElement('iframe');
    iframe.src = embedUrl;
    iframe.width = '100%'; // Set desired width
    iframe.height = '512'; // Set desired height
    iframe.allowFullscreen = true; // Optional: Allow fullscreen
    return iframe;
  }
}
